/**
 * What a client sees for a URL that does not exist, and for a route that throws.
 *
 * React Router's default is a developer screen — "Unexpected Application Error!
 * 404 Not Found" over a note telling *you* to add an errorElement. A client who
 * mistypes a link, or follows a stale one out of an email, was shown that. It
 * leaks the framework, offers no way back, and reads as though the site broke.
 *
 * `useRouteError` covers both cases this catches: a genuine 404 from an
 * unmatched path, and an exception thrown while rendering a real route. They
 * want different words — "this page doesn't exist" is wrong and slightly
 * alarming when the truth is "something went wrong loading your dossier" — so
 * the status is read off the error rather than assumed.
 */

import { isRouteErrorResponse, Link, useRouteError } from "react-router";

import { PublicFooter } from "./PublicFooter";

export function NotFound() {
  const error = useRouteError();
  // Rendered by the catch-all `*` route there is no route error at all (the
  // hook answers null) — the path simply matched nothing, the plainest "not
  // found" there is. It used to fall through to "That didn't load" for every
  // unknown URL.
  const missing =
    error == null || (isRouteErrorResponse(error) && error.status === 404);

  return (
    <main className="rp-notfound">
      <p className="rp-notfound__code">{missing ? "404" : "Something broke"}</p>
      <h1 className="rp-notfound__title">
        {missing ? "We couldn't find that page" : "That didn't load"}
      </h1>
      <p className="rp-notfound__body">
        {missing
          ? "The link may be out of date, or the address may have a typo in it. Nothing has happened to your account or your documents."
          : "Something went wrong on our side. Your account and documents are safe — try again, and if it keeps happening your agent can help."}
      </p>
      <div className="rp-notfound__actions">
        <Link to="/" className="rp-btn rp-btn--primary">
          Go to my home
        </Link>
        {!missing && (
          <button
            type="button"
            className="rp-btn rp-btn--secondary"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        )}
      </div>
      <PublicFooter />
    </main>
  );
}
